import { useMemo } from 'react';
import type { CrossProductSearchDialogProps } from '@atlassian/product-search-dialog';
import { N0, Opacity2, Opacity4, OpacityDark1 } from '@trello/colors';
import { useWorkspace } from '@trello/workspaces';
import { THEME_LIGHT, useThemeVariables } from 'app/src/components/Theme';
import { usePreviousWhileLoading } from 'app/src/components/WorkspaceNavigation/usePreviousWhileLoading';

export type SearchTheme = NonNullable<CrossProductSearchDialogProps['theme']>;

const defaultBorderColor = 'rgba(255, 255, 255, 0.25)';

export const getBaseSearchTheme = (): SearchTheme => ({
  default: {
    backgroundColor: Opacity2,
    borderColor: defaultBorderColor,
    color: N0,
  },
  focus: {},
  hover: {
    backgroundColor: Opacity4,
    borderColor: defaultBorderColor,
  },
});

/**
 * Generates a theme for the CrossProductSearch input component based on Trello
 * context. The theme requires three "states", though it simply calls lodash
 * merge on its base theme and the input, so any styles omitted will fall back
 * to their defaults (which are derived from Atlassian navigation). States:
 *
 * default: Styles for the input without any user interaction (hover, focus).
 *          Since the component won't have any text input in this state, `color`
 *          is used exclusively for styling the placeholder text and icon.
 *          On boards, we color these to blend into the colorful header.
 *
 * focus:   Styles for the input when it is active, and the search dialog is
 *          expanded. Because we have no ability to style the dialog, and only
 *          the input, we leave this empty to fall back to the default styles.
 *
 * hover:   A total misnomer: despite its name, these styles are exclusively
 *          referenced for the mobile takeover flow, and not at all for hover
 *          styling. This probably can't be changed without a major bump, but
 *          we're opting out of the mobile takeover in Trello, so it doesn't
 *          much matter what goes in here today. We'll keep it in sync with
 *          the way hover coloring _should_ work, and hopefully one day it will.
 */
export const useSearchTheme = (): SearchTheme => {
  const [themeVariables] = useThemeVariables();

  const workspace = useWorkspace();
  const idBoard = usePreviousWhileLoading(
    workspace.idBoard,
    workspace.isLoading,
    null,
  );

  return useMemo(() => {
    const searchTheme = getBaseSearchTheme();
    const isOnBoardView = !!idBoard;

    if (isOnBoardView) {
      searchTheme.default.color = themeVariables.dynamicTextColor;
    }

    if (isOnBoardView && themeVariables.theme === THEME_LIGHT) {
      searchTheme.default.backgroundColor = 'rgba(0, 0, 0, 0.04)';
      searchTheme.default.borderColor = 'rgba(0, 0, 0, 0.08)';

      searchTheme.hover.backgroundColor = OpacityDark1;
      searchTheme.hover.borderColor = 'rgba(0, 0, 0, 0.08)';
    }

    return searchTheme;
  }, [idBoard, themeVariables.theme, themeVariables.dynamicTextColor]);
};
